import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { callApi } from "../callEndpoint";
import {
  appointmentEndpoints,
  type AppointmentsRequest,
  type AppointmentDashboardResponse,
  type AppointmentResponse,
  type AppointmentsPostRequest,
  type AppointmentProposeNewDoctorRequest,
  type AppointmentsRescheduleRequest,
  type AppointmentsStatusRequest,
  type CheckPaymentStatusResponse,
} from "../contracts/appointments.contract";

const APPOINTMENTS_KEY = ["appointments"];
const PAGE_SIZE = 20;

function useInvalidatingMutation<T, R>(
  mutationFn: (payload: T) => Promise<R>,
  getIds?: (vars: T) => string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: APPOINTMENTS_KEY });
      const id = getIds?.(vars);
      if (id) {
        qc.invalidateQueries({ queryKey: ["appointment", id] });
        qc.invalidateQueries({ queryKey: ["payment-status", id] });
      }
    },
  });
}

export const useAppointments = (
  params: Omit<AppointmentsRequest, "take" | "skip">,
) =>
  useInfiniteQuery({
    queryKey: ["appointments", params],
    queryFn: async ({ pageParam = 0 }) => {
      const data = await callApi<AppointmentDashboardResponse[]>(
        appointmentEndpoints.list,
        { params: { ...params, take: PAGE_SIZE, skip: pageParam } },
      );
      return data ?? [];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_SIZE
        ? undefined
        : allPages.reduce((sum, p) => sum + p.length, 0),
  });

export const useAppointmentById = (id: string) =>
  useQuery({
    queryKey: ["appointment", id],
    queryFn: () =>
      callApi<AppointmentResponse>(appointmentEndpoints.getById(id)),
    enabled: !!id,
  });

export const useCheckPaymentStatus = (
  id: string | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: ["payment-status", id],
    queryFn: () =>
      callApi<CheckPaymentStatusResponse>(
        appointmentEndpoints.checkPaymentStatus(id!),
      ),
    enabled: !!id && enabled,
  });

export const useCreateAppointment = () =>
  useInvalidatingMutation((payload: AppointmentsPostRequest) =>
    callApi<{ id: string }>(appointmentEndpoints.create, { data: payload }),
  );

export const useRequestAppointmentMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AppointmentsPostRequest) => {
      const res = await callApi<{ appointmentId: string }>(
        appointmentEndpoints.create,
        { data: input },
      );
      return res.appointmentId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customer-appointments"] });
    },
  });
};

export const useRescheduleAppointment = () =>
  useInvalidatingMutation(
    (payload: AppointmentsRescheduleRequest) =>
      callApi(appointmentEndpoints.reschedule, { data: payload }),
    (v) => v.appointmentId,
  );

export const useCancelAppointment = () =>
  useInvalidatingMutation(
    (payload: AppointmentsStatusRequest) =>
      callApi(appointmentEndpoints.cancelByCustomer, { data: payload }),
    (v) => v.appointmentId,
  );

export const useCancelByAdmin = () =>
  useInvalidatingMutation(
    (payload: AppointmentsStatusRequest) =>
      callApi(appointmentEndpoints.cancelByAdmin, { data: payload }),
    (v) => v.appointmentId,
  );

export const useCompleteAppointment = () =>
  useInvalidatingMutation(
    (payload: AppointmentsStatusRequest) =>
      callApi(appointmentEndpoints.complete, { data: payload }),
    (v) => v.appointmentId,
  );

export const useNoShow = () =>
  useInvalidatingMutation(
    (payload: AppointmentsStatusRequest) =>
      callApi(appointmentEndpoints.noShow, { data: payload }),
    (v) => v.appointmentId,
  );

export const useMarkAsPaid = () =>
  useInvalidatingMutation(
    (payload: AppointmentsStatusRequest) =>
      callApi(appointmentEndpoints.markAsPaid, { data: payload }),
    (v) => v.appointmentId,
  );

export const useSendPaymentLink = () =>
  useInvalidatingMutation(
    (payload: AppointmentsStatusRequest) =>
      callApi(appointmentEndpoints.sendConfirmationLink, { data: payload }),
    (v) => v.appointmentId,
  );

/**
 * Admin "Confirm appointment": charges the flat $50 approval fee off-session
 * against the patient's saved card and moves the appointment to Approved. The
 * API throws 400 when the patient has no card (fall back to a payment link) or
 * the charge fails — callers should branch on the error message.
 */
export const useConfirmAndCharge = () =>
  useInvalidatingMutation(
    (payload: AppointmentsStatusRequest) =>
      callApi(appointmentEndpoints.confirmAndCharge, { data: payload }),
    (v) => v.appointmentId,
  );

export const useProposeNewDoctor = () =>
  useInvalidatingMutation(
    (payload: AppointmentProposeNewDoctorRequest) =>
      callApi(appointmentEndpoints.proposeNewDoctor, { data: payload }),
    (v) => v.currentAppointmentId,
  );

export const useGeneratePaymentLink = () =>
  useMutation({
    mutationFn: (payload: AppointmentsStatusRequest) =>
      callApi<string>(appointmentEndpoints.generatePaymentLink, {
        data: payload,
      }),
  });
